/**
 * Разбор ответа матчера на уровне ОДНОГО кандидата: чем он совпал, чем не
 * совпал, сколько за ним денег и как отфильтровать девяносто таких.
 *
 * Всё здесь — чистые функции без React: на боевом кейсе кандидатов до 99, и
 * каждое правило («ни одно списание не прошло», «все совпадения вытянуты
 * допуском») читается И карточкой как пометка, И фильтром как условие. Два
 * места, одно правило: разъехавшись, фильтр «только с прошедшим списанием»
 * прятал бы кандидатов, у которых карточка не показывает соответствующей
 * пометки, — и оператор не понял бы, куда делся человек.
 */
/**
 * Легаси-псевдоним апстрима: в снимках до 2026-08-25 последние четыре цифры
 * приезжают как `card_last4`, после — как `last4`. Для нас это ОДИН сигнал, и
 * сравнивать «спросили last4» с «совпало card_last4» без нормализации значит
 * объявить не подтвердившимся то, что подтвердилось.
 */
export function normalizeMatchSignal(signal) {
    return signal === 'card_last4' ? 'last4' : signal;
}
/**
 * Порядок сигналов по ДОКАЗАТЕЛЬНОСТИ, тот же, что у адаптера матчера
 * (`SIGNAL_ORDER`): идентификатор транзакции в сети уникален, код авторизации
 * почти уникален, а дата с суммой сами по себе не различают никого.
 */
const SIGNAL_ORDER = ['networkId', 'authCode', 'last4', 'date', 'amount'];
/**
 * Сигналы, которые ДОКАЗЫВАЮТ совпадение, а не подкрепляют его. Ровно те же,
 * что адаптер называет селективными: остальные три («карта + дата + сумма») в
 * сутки дают десятки списаний — из-за них очередь ручного разбора и выглядит
 * как список на девяносто человек.
 */
export const STRONG_MATCH_SIGNALS = ['networkId', 'authCode'];
/** Сортировка набора сигналов по доказательности; незнакомые — в конец. */
export function orderMatchSignals(signals) {
    const uniq = [...new Set([...signals].map(normalizeMatchSignal))];
    const known = SIGNAL_ORDER.filter((s) => uniq.includes(s));
    const unknown = uniq.filter((s) => !SIGNAL_ORDER.includes(s)).sort();
    return [...known, ...unknown];
}
/**
 * Все сигналы кандидата: и его собственные, и собранные по списаниям.
 *
 * Объединение, а не одно из двух: у снимка старше 2026-08-25 списаний нет
 * вовсе и заполнен только `candidate.matchedBy`, а у нового разбивка живёт по
 * позициям — и «совпал код авторизации» там видно только в списании.
 */
export function candidateMatchedSignals(candidate) {
    return orderMatchSignals([
        ...candidate.matchedBy,
        ...candidate.charges.flatMap((ch) => ch.matchedBy),
    ]);
}
/** Совпал ли кандидат хоть одним доказательным сигналом. */
export function hasStrongMatch(candidate) {
    const signals = new Set(candidateMatchedSignals(candidate));
    return STRONG_MATCH_SIGNALS.some((s) => signals.has(s));
}
/**
 * Ни одно списание кандидата НЕ прошло — «денег не было», и для чарджбека это
 * почти всегда не тот человек.
 *
 * Правило именно «нет ни одного удачного при наличии сорвавшегося», а не «есть
 * хотя бы одно непрошедшее»: у кандидата с тремя удачными списаниями и одной
 * сорвавшейся попыткой утверждение было бы ложью. Пустой `status` (снимок
 * старше поля) под правило не подпадает — молчание апстрима не повод объявить,
 * что денег не было.
 */
export function hasNoSuccessfulCharge(candidate) {
    const charges = candidate.charges;
    return (charges.some((ch) => ch.status === 'failed') && !charges.some((ch) => ch.status === 'succeeded'));
}
/**
 * Все совпадения вытянуты ДОПУСКОМ по дате: явный «соседний день» есть, а
 * точного нет ни одного.
 *
 * Требуется ЯВНЫЙ `near`, а не «нет ни одного `exact`»: у снимка старше поля
 * `dateMatch` пуст у всех списаний, и правило «нет точного» объявило бы
 * соседним днём то, о чём апстрим не сказал ничего.
 */
export function isNearDateOnly(candidate) {
    const charges = candidate.charges;
    return (charges.some((ch) => ch.dateMatch === 'near') && !charges.some((ch) => ch.dateMatch === 'exact'));
}
/** По какому-то списанию кандидата уже открыт спор. */
export function hasDisputedCharge(candidate) {
    return candidate.charges.some((ch) => ch.disputed === true);
}
/** По какому-то списанию кандидата уже сделан возврат. */
export function hasRefundedCharge(candidate) {
    return candidate.charges.some((ch) => ch.refunded === true);
}
// ───────────────────── Что у кандидатов вообще проверяли ────────────────────
const clean = (v) => {
    const t = v?.trim() ?? '';
    return t === '' ? null : t;
};
/**
 * Признаки, о которых по этой выдаче ТОЧНО известно, что их проверяли.
 *
 * Выводится из самого снимка: если хоть один кандидат кейса совпал по признаку,
 * значит признак у матчера спрашивали. Набор признаков запроса до карточки не
 * доезжает — он живёт внутри адаптера (`buildCriteria`), в ответе не сохраняется
 * и в `RiskCaseDetailDTO` его нет, — а у 39 уже накопленных снимков его не будет
 * и после правки контракта.
 *
 * Оценка ЗАНИЖАЕТ и никогда не завышает, и это её главное свойство. Признак,
 * не сошедшийся НИ У КОГО, в набор не попадёт, и мы про него просто промолчим —
 * это правильнее, чем объявить «не сошлось» о признаке, которого могло не быть
 * в запросе вовсе.
 *
 * Брать признаки из ПОЛЕЙ АЛЕРТА нельзя, и это не вопрос удобства: адаптер
 * отправляет не всё, что видит, — код авторизации с посторонними символами,
 * непохожие на себя четыре цифры, дробная сумма не уезжают в запрос вовсе. Такой
 * признак не «не сошёлся», его не спрашивали. Повторять же правила валидации
 * здесь значило бы завести вторую копию, которая разъедется при первой правке
 * адаптера.
 */
export function checkedMatchSignals(candidates) {
    return orderMatchSignals(candidates.flatMap(candidateMatchedSignals));
}
/**
 * Расхождение суммы, которое апстрим ОБЪЯСНИЛ: те же деньги, просто в других.
 * Такое расхождение доводом против кандидата не является и в «не сошлось» не
 * попадает — оно уже подписано у самого списания.
 */
const EXPLAINED_AMOUNT_KINDS = ['exact', 'fee-markup', 'fx-plausible'];
const hasExplainedAmount = (charges) => charges.some((ch) => ch.amountMatch != null && EXPLAINED_AMOUNT_KINDS.includes(ch.amountMatch.kind));
/**
 * Дату СРАВНИВАЛИ и вердикт по ней есть — «день в день» либо «соседний день».
 *
 * Такая дата в «не сошлось» не попадает, даже если апстрим не назвал её среди
 * `matchedBy`: вердикт уже стоит плашкой у самого списания, и вторая строка
 * «не сошлось: дата» рядом с плашкой «Соседний день» прямо ей противоречит —
 * оператор читает два взаимоисключающих утверждения об одном поле.
 */
const hasDateVerdict = (charges) => charges.some((ch) => ch.dateMatch != null);
/**
 * Признаки, которые у ДРУГИХ кандидатов этой выдачи сошлись, а у этого — нет.
 *
 * Это довод ПРОТИВ, и до сих пор он был невидим: карточка показывала только
 * совпавшее, а отсутствие признака в списке неотличимо от «его и не проверяли».
 * Оператор отказывает кандидату именно по обратному — «код авторизации сверяли,
 * и у него он не сошёлся».
 *
 * Утверждение здесь всегда о ПРОВЕРЕННОМ: набор берётся из
 * {@link checkedMatchSignals}, то есть из признаков, по которым в этой же
 * выдаче кто-то совпал. Отсутствие строки означает «неизвестно» и остаётся
 * молчанием — «неизвестно» отдельной подписью не пишется, это факт о наших
 * данных, а не о кандидате.
 *
 * Два вычета — там, где карточка иначе противоречила бы сама себе: сумма,
 * расхождение которой апстрим объяснил, и дата, по которой у списания уже стоит
 * собственный вердикт.
 */
export function unmatchedCheckedSignals(candidate, checked) {
    const matched = new Set(candidateMatchedSignals(candidate));
    return orderMatchSignals(checked.filter((s) => {
        if (matched.has(s))
            return false;
        if (s === 'amount' && hasExplainedAmount(candidate.charges))
            return false;
        if (s === 'date' && hasDateVerdict(candidate.charges))
            return false;
        return true;
    }));
}
export function summarizeCharges(charges) {
    const byCurrency = new Map();
    let failedCount = 0;
    for (const ch of charges) {
        if (ch.status === 'failed') {
            failedCount += 1;
            continue;
        }
        if (ch.amountCents == null || ch.currency == null)
            continue;
        byCurrency.set(ch.currency, (byCurrency.get(ch.currency) ?? 0) + ch.amountCents);
    }
    return {
        count: charges.length,
        failedCount,
        // Порядок валют детерминирован: иначе две загрузки одного кейса печатают
        // итог по-разному, и оператор ищет разницу там, где её нет.
        totals: [...byCurrency.entries()]
            .map(([currency, cents]) => ({ currency, cents }))
            .sort((a, b) => a.currency.localeCompare(b.currency)),
    };
}
/**
 * Сумма всех непрошедших мимо кассы списаний ОДНИМ числом — только для
 * сортировки.
 *
 * Валюты складываются как есть, и это допустимо ровно потому, что сравнение
 * идёт ВНУТРИ одного кейса: кейс — это одна транзакция одного эмитента, и
 * разные валюты у его кандидатов на боевых данных не встречаются. Показывать
 * это число нельзя — для показа есть `summarizeCharges().totals` с валютами.
 */
const sortableAmount = (candidate) => summarizeCharges(candidate.charges).totals.reduce((n, t) => n + t.cents, 0);
/** Самое свежее списание кандидата, миллисекундами; без списаний — `null`. */
const latestChargeAt = (candidate) => {
    let max = null;
    for (const ch of candidate.charges) {
        if (ch.createdAt == null)
            continue;
        const t = Date.parse(ch.createdAt);
        if (Number.isNaN(t))
            continue;
        if (max === null || t > max)
            max = t;
    }
    return max;
};
/**
 * Вес уверенности апстрима. `null` — «снимок старше поля», и он тяжелее
 * `none` («ничего не сходится») быть не может, но и легче тоже: это разные
 * факты, поэтому у них разные ступени, а не одна.
 */
const CONFIDENCE_RANK = {
    confirmed: 5,
    high: 4,
    medium: 3,
    low: 2,
    none: 1,
};
const confidenceRank = (candidate) => candidate.confidence == null ? 0 : (CONFIDENCE_RANK[candidate.confidence] ?? 0);
export const EMPTY_CANDIDATE_FILTERS = {
    q: '',
    onlySettled: false,
    onlyExactDate: false,
    onlyStrongSignal: false,
    sort: 'confidence',
};
/**
 * Сужена ли выборка. Сортировка сюда НЕ входит: она меняет порядок, но не
 * состав, и предлагать «сбросить фильтры» из-за неё значило бы обещать
 * вернуть кандидатов, которые никуда не девались.
 */
export function hasActiveCandidateFilters(s) {
    return s.q.trim() !== '' || s.onlySettled || s.onlyExactDate || s.onlyStrongSignal;
}
/**
 * Всё, по чему кандидат ищется подстрокой. Идентификаторы списаний включены
 * намеренно: оператор приходит в диалог с открытым платёжным сервисом в
 * соседней вкладке и вставляет оттуда `ch_…` или id транзакции в сети.
 */
const searchHaystack = (c) => [
    c.email,
    c.stripeEmail,
    c.billingEmail,
    c.displayName,
    c.billingName,
    c.witUserId,
    c.stripeCustomerId,
    c.cardLast4,
    ...c.charges.flatMap((ch) => [
        ch.cardLast4,
        ch.walletLast4,
        ch.chargeId,
        ch.invoiceId,
        ch.networkTransactionId,
    ]),
]
    .filter((v) => v != null && v !== '')
    .join(' ')
    .toLowerCase();
export function filterCandidates(candidates, s) {
    const q = s.q.trim().toLowerCase();
    return candidates.filter((c) => {
        if (q !== '' && !searchHaystack(c).includes(q))
            return false;
        if (s.onlySettled && hasNoSuccessfulCharge(c))
            return false;
        if (s.onlyExactDate && isNearDateOnly(c))
            return false;
        if (s.onlyStrongSignal && !hasStrongMatch(c))
            return false;
        return true;
    });
}
/**
 * Порядок показа. Сортировка СТАБИЛЬНАЯ (`Array.prototype.sort` таков по
 * стандарту), и это существенно: внутри одной ступени сохраняется порядок
 * апстрима, который сам по себе осмыслен — он уже отсортирован по силе
 * совпадения. Поэтому у снимка, где уверенности нет ни у кого, «по силе
 * совпадения» не перемешивает список, а оставляет его как прислали.
 */
export function sortCandidates(candidates, sort) {
    const list = [...candidates];
    if (sort === 'date') {
        // Без даты — в конец: «неизвестно когда» не должно вклиниваться между
        // вчера и позавчера.
        return list.sort((a, b) => (latestChargeAt(b) ?? -Infinity) - (latestChargeAt(a) ?? -Infinity));
    }
    if (sort === 'amount')
        return list.sort((a, b) => sortableAmount(b) - sortableAmount(a));
    return list.sort((a, b) => confidenceRank(b) - confidenceRank(a) ||
        // Доказательный сигнал перевешивает при равной уверенности: апстрим
        // ставит `high` и совпавшему коду авторизации, и совпавшей паре
        // «карта + дата», а различает их только это.
        Number(hasStrongMatch(b)) - Number(hasStrongMatch(a)));
}
export function applyCandidateFilters(candidates, s) {
    return sortCandidates(filterCandidates(candidates, s), s.sort);
}
// ─────────────────────── Устойчивость кандидата в списке ────────────────────
/**
 * Ключ кандидата для React — устойчивый к ФИЛЬТРАЦИИ и СОРТИРОВКЕ.
 *
 * Индекс в показанном списке ключом быть НЕ МОЖЕТ, и это не про
 * производительность. Список фильтруется и сортируется на месте, позиции при
 * этом съезжают, и ключ вида `почта-<позиция>` начинает указывать на другого
 * человека. React честно переиспользует под этим ключом прежний узел вместе с
 * его локальным состоянием — то есть развёрнутые списания ОДНОГО кандидата
 * оказываются под именем ДРУГОГО. Для экрана, где решение «этот или не этот»
 * принимают по списаниям, это худший из возможных сбоев: он не выглядит
 * ошибкой.
 *
 * Отсюда состав: сначала настоящие идентификаторы, потом почта и цифры карты,
 * и лишь в самом конце — позиция в ИСХОДНОМ (нефильтрованном) массиве, которая
 * под фильтрацией не меняется. Совпадение ключа у двух кандидатов, у которых
 * пусто вообще всё, безвредно: такие строки неразличимы и по содержимому.
 */
export function candidateKey(candidate, originalIndex) {
    const stable = [
        candidate.witUserId,
        candidate.stripeCustomerId,
        candidate.email,
        candidate.charges[0]?.chargeId ?? null,
        candidate.cardLast4,
    ]
        .map(clean)
        .find((v) => v != null);
    return stable ?? `позиция-${originalIndex}`;
}
/**
 * Ключи всех кандидатов кейса, посчитанные ОДИН раз по исходному массиву.
 *
 * Отдельная функция, потому что позиция берётся именно из него: считать её по
 * отфильтрованному списку значило бы вернуть ровно ту ошибку, от которой
 * ключ и защищает.
 */
export function candidateKeys(candidates) {
    const m = new Map();
    candidates.forEach((c, i) => m.set(c, candidateKey(c, i)));
    return m;
}
export function candidateIdentityLines(candidate) {
    // Порядок адресов задан: основной, затем из Stripe, затем биллинговый —
    // так же, как их показывала карточка до этой правки.
    const emails = [
        ...new Set([candidate.email, candidate.stripeEmail, candidate.billingEmail]
            .map(clean)
            .filter((v) => v != null)),
    ];
    const name = clean(candidate.displayName) ?? clean(candidate.billingName);
    if (name != null) {
        return {
            primary: name,
            primaryIsEmail: false,
            secondary: emails[0] ?? null,
            otherEmails: emails.slice(1),
        };
    }
    if (emails.length > 0) {
        return {
            primary: emails[0],
            primaryIsEmail: true,
            secondary: null,
            otherEmails: emails.slice(1),
        };
    }
    const fallback = clean(candidate.witUserId) ??
        clean(candidate.stripeCustomerId) ??
        candidate.charges.map((ch) => clean(ch.chargeId)).find((v) => v != null) ??
        null;
    return {
        primary: fallback ?? 'Клиент без имени и почты',
        primaryIsEmail: false,
        secondary: null,
        otherEmails: [],
    };
}
// ─────────────────────── Развязки «этот или не этот» ────────────────────────
/**
 * Кандидат — ЕДИНСТВЕННЫЙ в кейсе, кто совпал доказательным сигналом.
 *
 * Прямой ответ на вопрос, ради которого диалог и открывают: из девяноста
 * человек, совпавших «картой и датой», один совпал кодом авторизации — выбор
 * сделан. Считается по ПОЛНОМУ списку кандидатов, а не по отфильтрованному:
 * «единственный среди показанных» — свойство фильтра, а не кейса.
 */
export function isOnlyStrongMatch(candidate, allCandidates) {
    if (!hasStrongMatch(candidate))
        return false;
    return allCandidates.filter((c) => hasStrongMatch(c)).length === 1;
}
/**
 * Почта кандидата совпала с почтой, которую назвал сам источник алерта.
 *
 * Сильная развязка и при этом НЕ сигнал матчера: по почте поиск не идёт вовсе
 * (эндпоинт её не принимает), так что совпадение — независимое подтверждение
 * с другой стороны. Сравнение регистронезависимое и по всем известным адресам
 * кандидата: биллинговая почта и почта в Stripe расходятся сплошь и рядом.
 */
export function matchesAlertEmail(candidate, alerts) {
    const alertEmails = new Set(alerts
        .map((a) => clean(a.providerCustomerEmail)?.toLowerCase())
        .filter((v) => v != null));
    if (alertEmails.size === 0)
        return false;
    return [candidate.email, candidate.stripeEmail, candidate.billingEmail].some((e) => {
        const v = clean(e)?.toLowerCase();
        return v != null && alertEmails.has(v);
    });
}
export function caseMatchContext(detail) {
    return {
        checked: checkedMatchSignals(detail.candidates),
        alerts: detail.alerts,
        candidates: detail.candidates,
    };
}
//# sourceMappingURL=candidate-match.js.map